"use client";

import { useAuth, useOrganization, useUser } from "@clerk/nextjs";
import posthog from "posthog-js";
import { useEffect, useState } from "react";

declare global {
  interface Window {
    __agentOpsPostHogInitialized?: boolean;
  }
}

const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";

export function PostHogAnalytics() {
  const { isLoaded, isSignedIn, user } = useUser();
  const { orgId, orgRole } = useAuth();
  const { organization } = useOrganization();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (window.__agentOpsPostHogInitialized) {
      setInitialized(true);
      return;
    }
    const key = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN
      || process.env.NEXT_PUBLIC_POSTHOG_KEY;
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST || DEFAULT_POSTHOG_HOST;
    if (!key || !host) return;

    posthog.init(key, {
      api_host: host,
      defaults: "2026-01-30",
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      person_profiles: "identified_only",
      loaded: () => {
        window.__agentOpsPostHogInitialized = true;
        setInitialized(true);
      },
    });
  }, []);

  useEffect(() => {
    if (!initialized || !isLoaded) return;

    if (!isSignedIn || !user) {
      posthog.reset();
      return;
    }

    posthog.identify(user.id, {
      email: user.primaryEmailAddress?.emailAddress,
      name: user.fullName || user.username || user.primaryEmailAddress?.emailAddress || user.id,
      clerk_org_id: orgId || undefined,
      org_name: organization?.name || undefined,
      org_role: orgRole || undefined,
    });
    if (orgId) {
      posthog.group("company", orgId, {
        name: organization?.name || orgId,
        role: orgRole || undefined,
      });
    }
  }, [
    initialized,
    isLoaded,
    isSignedIn,
    orgId,
    orgRole,
    organization?.name,
    user?.fullName,
    user?.id,
    user?.primaryEmailAddress?.emailAddress,
    user?.username,
    user,
  ]);

  return null;
}
